import type { EmsComponents } from '../../api/ems';

export type Incident = EmsComponents['schemas']['NG_SOS.Incidents.Domain.Abstractions.PortalIncident'];

export type Caller = Incident['caller'];
export type Patient = NonNullable<Incident['patient']>;
export type Dispatcher = Incident['dispatcher'];
export type Position = Incident['positions'][number];
export type Attachment = Incident['attachments'][number];
export type AdditionalIncidentData = Incident['additionalIncidentData'];
export type IncidentPsap = Incident['involvedPsaps'][number];
export type CollaborationStateHistoryRecord = IncidentPsap['statusHistory'][number];
export type IncidentPsapView = Incident['psapViews'][number];

export type AppInfo = Caller['appInfo'];
export type DeviceInfo = Caller['deviceInfo'];
export type EmergencyContact = Caller['contacts'][number];
export type Classification = NonNullable<Caller['classification']>;
export type PermanentResidence = NonNullable<Caller['permanentResidence']>;
export type LiveVideo = Caller['liveVideos'][number];
export type MedicData = NonNullable<Patient['medicData']>;
export type TemporaryInformation = NonNullable<MedicData['temporaryInformation']>;
export type UserInfo = LiveVideo['startedByUser'];
export type GeoPoint = NonNullable<IncidentPsap['position']>;

export type IncidentState = Incident['state'];
export type PositionSource = Position['positionSource'];
export type CollaborationState = CollaborationStateHistoryRecord['state'];
export type LiveVideoState = Incident['videoState'];
export type LiveVideoSource = LiveVideo['source'];
export type AppType = AppInfo['type'];
export type DeviceOs = DeviceInfo['system'];
export type InternetConnectionType = DeviceInfo['internetConnectionType'];
export type Sex = MedicData['sex'];
export type BloodType = MedicData['bloodType'];

export type EedLiveVideo = Extract<LiveVideo, { source: 'Eed' }>;
export type WebAppLiveVideo = Extract<LiveVideo, { source: 'WebApp' }>;
export type ElsLiveVideo = Extract<LiveVideo, { source: 'Els' }>;

type ExactlyTheUnion<TUnion extends string> = { [TMember in TUnion]: TMember };

export const IncidentState = {
  Open: 'Open',
  Closed: 'Closed',
} as const satisfies ExactlyTheUnion<IncidentState>;

export const PositionSource = {
  Manual: 'Manual',
  MobileApp: 'MobileApp',
  WebApp: 'WebApp',
  Eed: 'Eed',
  Aml: 'Aml',
} as const satisfies ExactlyTheUnion<PositionSource>;

export const CollaborationState = {
  Added: 'Added',
  Requested: 'Requested',
  Confirmed: 'Confirmed',
  Rejected: 'Rejected',
  Supervisor: 'Supervisor',
} as const satisfies ExactlyTheUnion<CollaborationState>;

export const LiveVideoState = {
  Inactive: 'Inactive',
  Active: 'Active',
  Ended: 'Ended',
} as const satisfies ExactlyTheUnion<LiveVideoState>;

export const LiveVideoSource = {
  WebApp: 'WebApp',
  Eed: 'Eed',
  Els: 'Els',
} as const satisfies ExactlyTheUnion<LiveVideoSource>;

export const AppType = {
  Mobile: 'Mobile',
  Web: 'Web',
  IOT: 'IOT',
} as const satisfies ExactlyTheUnion<AppType>;

export const DeviceOs = {
  Unknown: 'Unknown',
  iOS: 'iOS',
  Android: 'Android',
  WindowsPhone: 'WindowsPhone',
  Aligator: 'Aligator',
} as const satisfies ExactlyTheUnion<DeviceOs>;

export const InternetConnectionType = {
  Unknown: 'Unknown',
  NoConnection: 'NoConnection',
  WiFi: 'WiFi',
  G2: 'G2',
  G3: 'G3',
  G4: 'G4',
  G5: 'G5',
} as const satisfies ExactlyTheUnion<InternetConnectionType>;

export const Sex = {
  Unknown: 'Unknown',
  Male: 'Male',
  Female: 'Female',
  Other: 'Other',
} as const satisfies ExactlyTheUnion<Sex>;

export const BloodType = {
  Unknown: 'Unknown',
  ZeroNegative: 'ZeroNegative',
  ZeroPositive: 'ZeroPositive',
  ANegative: 'ANegative',
  APositive: 'APositive',
  BNegative: 'BNegative',
  BPositive: 'BPositive',
  ABNegative: 'ABNegative',
  ABPositive: 'ABPositive',
} as const satisfies ExactlyTheUnion<BloodType>;
